import { Filter } from '@df/catalog-ui/listing/filter/filter';
import { ListingFilter } from '@df/catalog-ui/listing/listing';
import { IPageDataV2 } from '@df/catalog/cms/cms-model.interface';
import { NgInject } from '@df/ng/ng-inject';
import { ICmsPageResource } from '@icms/core/resource/page/icms-page.resource';
import compact from 'lodash-es/compact';
import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import { LocalICmsPage } from '../../../../catalog/page/local/local-page';
import { NBlogCategoryPageType } from '../../page/category-view/cms-blog-page-category-view.component';
import { NBlogPostPageType } from '../../page/post-view/cms-blog-page-post-view.component';

@ListingFilter({
    filterType: 'blogTag'
})
export class CmsBlogContentTagFilter extends Filter<LocalICmsPage<IPageDataV2<NBlogPostPageType.ITypeData>>, number[]> {
    /**
     * Initial selection
     */
    protected override _selection: number[] = [];

    /**
     * Category filter alway has matches
     */
    override hasAnyMatches: boolean = false;

    /**
     * Page resource
     */
    @NgInject(ICmsPageResource)
    private pageResource?: ICmsPageResource;

    /**
     * Tags (pages)
     */
    options?: LocalICmsPage<IPageDataV2<NBlogCategoryPageType.ITypeData>>[];

    /**
     * Retrive pages (blog categories) on init
     */
    protected override onInit() {
        const optionsIds = compact(uniq(flatten(this.listing.getSourceModels().map(i => i.getBlogPostCategoryIds()))));
        this.options =
            (this.pageResource?.getAllExisting(optionsIds) as LocalICmsPage<IPageDataV2<NBlogCategoryPageType.ITypeData>>[]) || [];
        this.hasAnyMatches = !!this.options.length;
        super.onInit();
    }

    /**
     * Applies filter to models collection
     */
    applyFilter(
        collection: LocalICmsPage<IPageDataV2<NBlogPostPageType.ITypeData>>[]
    ): LocalICmsPage<IPageDataV2<NBlogPostPageType.ITypeData>>[] {
        if (!this.hasSelection) {
            return collection;
        }
        // filter collection
        return collection.filter(model => !!model.getBlogPostCategoryIds()?.find(i => this.selection?.includes(i)));
    }

    getState(): number[] {
        return this._selection;
    }

    setState(data: string | number | (string | number)[]) {
        if (!data) {
            return;
        }
        if (Array.isArray(data)) {
            this.selection = <number[]>data;
        } else {
            if (typeof data === 'string') {
                this.selection = data.split(',').map(i => parseInt(i, 10));
            } else if (typeof data === 'number') {
                this.selection = [data];
            }
        }
    }

    reset() {
        this.selection = [];
        this.next();
    }

    select(optionId: number, unselectOther = true) {
        if (unselectOther) {
            this.selection = [optionId];
        } else {
            if (!this.isSelected(optionId)) {
                this.selection?.push(optionId);
            }
        }
        this.next();
    }

    unselect(optionId: number) {
        this.selection = this.selection?.filter(i => i !== optionId);
        this.next();
    }

    isSelected(optionId: number) {
        return this.selection?.includes(optionId);
    }

    /**
     * Checks if filter is applied
     * @return {boolean}
     */
    get hasSelection(): boolean {
        return !!this._selection.length;
    }

    /**
     * Idenfing function for options repeater
     */
    optionTrackByFn(index: number, option: LocalICmsPage) {
        return option.id;
    }
}
